<template>
  <form
    class="box"
    @submit.prevent="submit"
    :data-testid="`${modelName}-form`"
  >
    <h3 class="subtitle">
      {{ title }}
      <a
        v-if="closeButton"
        style="float: right"
        class="delete"
        @click="emit('close')"
      ></a>
    </h3>
    <FormInput
      v-for="(field, name) in fields"
      :key="name"
      :context="context"
      v-model="(
        // @ts-ignore
        localValue[name]
      )"
      :field="(field as FormFieldConfig)"
      :errResponse="(
        // @ts-ignore
        errResponse[name]
      )"
    >
    </FormInput>
    <!-- @slot space after the configured inputs, to add custom inputs that don't fit in the configuration -->
    <slot
      name="extra-form-inputs"
      :context="context"
    />
    <div
      v-if="errResponse.non_field_errors"
      class="notification is-danger"
    >
      <ul>
        <li
          v-for="error in errResponse.non_field_errors"
          :key="error"
        >
          {{ error }}
        </li>
      </ul>
    </div>
    <div class="buttons">
      <button
        class="button is-primary"
        type="submit"
      >Submit</button>
      <button
        type="button"
        @click="del"
        v-if="forUpdate && !disableDelete"
        class="button is-danger is-outlined"
      >
        Delete
      </button>
    </div>
  </form>
</template>

<script lang="ts">
/** The interface to handle updates, this matches with the ModelViewSet classes */
export interface FormClient<TCreate, TDetail extends DatabaseItem = TCreate & DatabaseItem, TUpdate extends DatabaseItem = TCreate & DatabaseItem> {
  create(data: TCreate): Promise<TDetail>
  update(data: TUpdate): Promise<TDetail>
  partialUpdate(data: Partial<TUpdate>): Promise<TDetail>
  delete(id: number): Promise<TDetail>
}

interface UpdateEvent<T> {
  action: 'create' | 'update'
  data: T
}

export interface DeleteEvent {
  action: 'delete'
  id: number
}

export type FormFormFieldConfig = FormFieldConfig & {
  resetValue?: any
}

export type FormFieldsConfig<TCreate, TUpdate = TCreate & DatabaseItem> = Partial<Record<keyof TCreate | keyof TUpdate, FormFormFieldConfig>>

export type FormSubmittedEvent<TDetail> = UpdateEvent<TDetail> | DeleteEvent

</script>

<script
  setup
  lang="ts"
  generic="TCreate extends object, TDetail extends DatabaseItem = TCreate & DatabaseItem, TUpdate extends DatabaseItem = TCreate & DatabaseItem"
>
import { computed, ref } from 'vue';
import type { DatabaseItem, Drf400Error } from '../crud';
import FormInput, { type FormFieldConfig } from './inputs/FormInput.vue';
import { ApiError } from '../backend';

const props = defineProps<{
  /** The model name is used to create the title of the form and generate the HTML ids */
  modelName: string
  /** The client should handle the calls for submit, update and delete */
  client: FormClient<TCreate, TDetail, TUpdate>
  /** Set to true to show a close button in the top right, that buttons emits the 'close' event */
  closeButton?: boolean
  /** Set to true to not show a delete button when an id is present in the modelValue */
  disableDelete?: boolean
  /** Set to true to submit a PATCH instead PUT request when an id is present in the modelValue */
  partialUpdate?: boolean
  /** The actual values of the form inputs, and potentially other fields */
  modelValue: Partial<TCreate | TUpdate>
  /** The configuration of the FormInput components */
  fields: FormFieldsConfig<TCreate, TUpdate>
}>()

const emit = defineEmits<{
  /** Emits the updated modelValue when a field is changed */
  (e: 'update:modelValue', data: Partial<TCreate | TUpdate>): void
  /** Emits when the close button is clicked */
  (e: 'close'): void
  /** Emits when the form is submitted, with the response from the server if ok */
  (e: 'form-submitted', data: FormSubmittedEvent<TDetail>): void
}>()

/** A type guard to check if we should create or update */
function forUpdateFn(item: Partial<TCreate | TUpdate>): item is TUpdate {
  if ('id' in item) {
    return true
  }
  return false
}
const forUpdate = computed(() => forUpdateFn(props.modelValue))

const title = computed(() => {
  if (forUpdate.value) {
    return "Update " + props.modelName
  } else {
    return "Add " + props.modelName
  }
})

/** The context is used to generate the html id's for the form inputs */
const context = computed(() => {
  return props.modelName.replaceAll(" ", "-").toLowerCase()
})

/** A computed copy of modelValue so we can use v-model on the FormInput component */
const localValue = computed({
  get() {
    return props.modelValue
  },
  set(newValue) {
    emit("update:modelValue", newValue)
  }
})

const errResponse = ref<Drf400Error>({})

/** Handle the submit button press */
async function submit() {
  errResponse.value = {}
  try {
    if (forUpdateFn(props.modelValue) && props.partialUpdate) {
      const response = await props.client.partialUpdate(props.modelValue)
      emit("form-submitted", { action: "update", data: response })
    } else if (forUpdateFn(props.modelValue)) {
      const response = await props.client.update(props.modelValue)
      emit("form-submitted", { action: "update", data: response })
    } else {
      // @ts-ignore, by now the modelValue should have all values filled in
      const response = await props.client.create(props.modelValue)
      emit("form-submitted", { action: "create", data: response })
    }
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.response.status === 400) {
        const response = await err.response.json()
        errResponse.value = response
        return
      }
    }
    errResponse.value = {
      non_field_errors: [`${err}`]
    }
  }
  resetForm()
}

/** Reset the form to the configured starting values */
function resetForm() {
  for (const [fieldName, configUntyped] of Object.entries(props.fields)) {
    const config = configUntyped as FormFormFieldConfig
    if (config.resetValue === undefined) continue
    // @ts-expect-error - localValue could be TCreate, while props.fields could be TUpdate
    // But implementers shouldn't do that.
    localValue.value[fieldName] = config.resetValue
  }
}

/** Handle the delete button press */
async function del() {
  if ('id' in props.modelValue && props.modelValue.id !== undefined) {
    await props.client.delete(props.modelValue.id)
    emit("form-submitted", { action: "delete", id: props.modelValue.id })
  }
}

</script>